import Image from 'next/image'
import { ChevronRightIcon } from 'primitives/icons'
import { Divider } from 'components/divider'
import { tw } from 'utils/classnames'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'

import DesignWithAiFree from '@web/assets/components/studio-menu/design-with-ai-free.jpg'
import DesignWithAiPro from '@web/assets/components/studio-menu/design-with-ai-pro.jpg'
import DesignWithAiTeams from '@web/assets/components/studio-menu/design-with-ai-teams.jpg'
import TShirtCreator from '@web/assets/components/studio-menu/t-shirt-creator.jpg'
import LogoCreator from '@web/assets/components/studio-menu/logo-creator.jpg'

export function NavigationLink({
  name,
  href,
  className = '',
  linkClassName = '',
}: {
  name: React.ReactNode
  href: string
  className?: string
  linkClassName?: string
}) {
  return (
    <li
      className={tw(
        'text-14 rounded-2 flex h-[40px] w-full cursor-pointer items-center p-2 text-gray-600 hover:bg-gray-900 hover:text-violet-500',
        className,
      )}
    >
      <Link href={href} className={linkClassName}>
        {name}
      </Link>
    </li>
  )
}

export function NavigationLinkWithIcon({
  name,
  href,
  image,
  subtitle,
}: {
  name: string
  href: string
  image: string
  subtitle: string
}) {
  return (
    <li className="rounded-2 group flex w-full cursor-pointer items-center p-2 hover:bg-gray-800">
      <Link className="flex items-center gap-3" href={href}>
        <Image
          src={image}
          alt={name}
          width={100}
          height={100}
          className="rounded-1 h-[50px] w-auto"
        />
        <div className="flex flex-col justify-center gap-1">
          <span className="text-14 font-[500] text-gray-400 group-hover:text-violet-500">
            {name}
          </span>
          <span className="text-12 text-gray-500">{subtitle}</span>
        </div>
      </Link>
    </li>
  )
}

export function NavigationLinkWithImage({
  name,
  href,
  image,
  subtitle,
}: {
  name: string
  href: string
  image: string
  subtitle: string
}) {
  return (
    <li className="rounded-2 group flex cursor-pointer p-2 hover:bg-gray-800">
      <Link href={href} className="flex max-w-[260px] flex-col gap-4 hover:bg-gray-800">
        <Image src={image} width={260} height={200} alt={name} className="rounded-2" />
        <div className="flex flex-col gap-1">
          <div className="flex flex-row justify-between">
            <span className="text-14 font-semibold text-gray-100 group-hover:text-violet-500">
              {name}
            </span>
            <ChevronRightIcon className="h-[24px] w-[24px] text-gray-100" aria-hidden />
          </div>
          <p className="text-12 text-gray-500">{subtitle}</p>
        </div>
      </Link>
    </li>
  )
}

export function DesignWithAiCustom() {
  return (
    <div className="flex flex-col xl:flex-row">
      <div className="flex flex-col gap-4 xl:flex-row">
        <NavigationLinkWithImage
          name="Free"
          href={AppRoutes.STUDIO_TOOL}
          image={DesignWithAiFree.src}
          subtitle="For anyone to design anything, on their own or with family, friends, or others. No experience required."
        />
        <NavigationLinkWithImage
          name="Pro"
          href={AppRoutes.STUDIO_TOOL}
          image={DesignWithAiPro.src}
          subtitle="For individuals wanting unlimited access to premium content and design tools."
        />
        <NavigationLinkWithImage
          name="Teams"
          href={AppRoutes.STUDIO_TOOL}
          image={DesignWithAiTeams.src}
          subtitle="For teams of all sizes wanting to create together, with premium workplace and brand tools."
        />
      </div>
      <Divider orientation="vertical" className="ml-4 mr-1 hidden xl:block" />
      <Divider className="my-2 block xl:hidden" />
      <div className="flex flex-col gap-1 xl:max-w-[230px]">
        <NavigationLinkWithIcon
          image={TShirtCreator.src}
          href={AppRoutes.STUDIO_TOOL}
          name="Education"
          subtitle="A powerful, free tool for classrooms, districts and students."
        />
        <NavigationLinkWithIcon
          image={LogoCreator.src}
          href={AppRoutes.STUDIO_TOOL}
          name="Not-for-profit"
          subtitle="CF Studio is free for all registered nonprofits. Apply today."
        />
      </div>
    </div>
  )
}

export function PodCustom() {
  return (
    <div className="flex flex-col xl:flex-row xl:gap-4">
      <div className="flex w-max flex-col gap-1">
        <NavigationLink name="Documents" href={AppRoutes.STUDIO_TOOL} className="font-semibold" />
        <NavigationLink name="Visual Suite" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Docs" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Presentation" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Table" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="PDF Editor" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Charts" href={AppRoutes.STUDIO_TOOL} />
      </div>
      <Divider className="my-2 block xl:hidden" />
      <div className="flex w-max flex-col gap-1">
        <NavigationLink
          name="Photos & Videos"
          href={AppRoutes.STUDIO_TOOL}
          className="font-semibold"
        />
        <NavigationLink name="Video Editor" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Youtube Editor" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Photos Editor" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Different Photos" href={AppRoutes.STUDIO_TOOL} />
      </div>
      <Divider className="my-2 block xl:hidden" />
      <div className="flex w-max flex-col gap-1">
        <NavigationLink name="Print" href={AppRoutes.STUDIO_TOOL} className="font-semibold" />
        <NavigationLink name="Business Cards" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Cards" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Invitations" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Mugs" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="T-Shirts" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Hoodies" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Calendars" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Labels" href={AppRoutes.STUDIO_TOOL} />
      </div>
      <Divider className="my-2 block xl:hidden" />
      <div className="flex w-max flex-col gap-1">
        <NavigationLink name="Marketing" href={AppRoutes.STUDIO_TOOL} className="font-semibold" />
        <NavigationLink name="Logos" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Posters" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Flyers" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Brochures" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Social Media" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Websites" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Stickers" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="Yard Signs" href={AppRoutes.STUDIO_TOOL} />
        <NavigationLink name="QR Code Generator" href={AppRoutes.STUDIO_TOOL} />
      </div>
      <Divider orientation="vertical" className="hidden flex-shrink-0 self-stretch xl:block" />
      <Divider className="my-2 block xl:hidden" />
      <div className="flex h-fit flex-col xl:flex-row xl:gap-4">
        <NavigationLinkWithImage
          name="Photos & Videos"
          href={AppRoutes.STUDIO_TOOL}
          image={DesignWithAiPro.src}
          subtitle="Choose from thousands of free, ready-to-use templates."
        />
        <NavigationLinkWithImage
          name="Photos & Videos"
          href={AppRoutes.STUDIO_TOOL}
          image={DesignWithAiTeams.src}
          subtitle="Choose from thousands of free, ready-to-use templates.."
        />
      </div>
    </div>
  )
}
